import React, { useEffect, useState } from "react";
import { CloseIcon } from "../../../assets/icons";
import BannerIcon from "../../../assets/icons/bannerIcon";
import CheckBlueIcon from "../../../assets/icons/CheckBlueIcon";
import AlertIcon from "../../../assets/icons/AlertIcon";
import HourGlassIcon from "../../../assets/icons/HourglassIcon";
import { setToastHandler } from "../../../utils/toastUtils";

export type ToastType = "success" | "error" | "loading" | "info";

export interface ToastProps {
  message: string;
  type: ToastType;
  actionText?: string;
  onActionClick?: () => void;
  onClose?: () => void;
  duration?: number;
  position?: "top" | "bottom";
}

const Toast: React.FC<ToastProps> = ({
  message,
  type,
  actionText,
  onActionClick,
  onClose,
  duration = 5000,
  position = "top",
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isHover, setIsHover] = useState(false);
  const [toastLoaderDuration, setToastLoaderDuration] = useState("5s");
  const [width, setWidth] = useState("1px");
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    setWidth("450px");
    setToastLoaderDuration(`${duration / 1000}s`);
  }, [duration]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsComplete(true);
    }, duration);

    return () => clearTimeout(timer);
  }, [onClose, duration, type]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      if (onClose) onClose();
    }, 300);
  };

  useEffect(() => {
    if (onClose && type !== "loading") {
      // Only check isHover state when duration completes
      if ((!isHover || !actionText) && isComplete) {
        setIsVisible(false);
        onClose();
        setIsComplete(false);
      }
    }
  }, [isHover, isComplete]);

  if (!isVisible) return null;

  const getToastStyles = () => {
    switch (type) {
      case "success":
        return {
          bgColor: "bg-[#ECFDF3]",
          loadingColor: "bg-green-500",
          textColor: "text-[#027A48]",
          icon: <BannerIcon />,
        };
      case "error":
        return {
          bgColor: "bg-[#FFDAD6]",
          loadingColor: "bg-red-500",
          textColor: "text-[#B42318]",
          icon: <AlertIcon />,
        };
      case "loading":
        return {
          bgColor: "bg-[#FFF8E6]",
          loadingColor: "bg-amber-500",
          textColor: "text-[#B86200]",
          icon: <HourGlassIcon className="spin" color="#B86200" />,
        };
      case "info":
        return {
          bgColor: "bg-[#F0F7FF]",
          loadingColor: "bg-sky-600",
          textColor: "text-[#084784]",
          icon: <CheckBlueIcon />,
        }; 
      default:
        return {
          bgColor: "bg-[#D1FADF]",
          loadingColor: "bg-green-600",
          textColor: "text-[#025932]",
          icon: <BannerIcon />,
        };
    }
  };

  const { bgColor, loadingColor, textColor, icon } = getToastStyles();

  const positionClass =
    position === "top" ? "sm:top-6 top-4" : "sm:bottom-6 bottom-4";

  const toastMaxWidth = {
    maxWidth: width,
  };
  const toastLoaderStyle = {
    width: width,
    transition: `width ${toastLoaderDuration} linear`,
  };

  return (
    <div
      className={`fixed w-screen z-[200] pointer-events-none ${positionClass} flex items-center justify-center transition-opacity delay-300 sm:px-6 px-4 ${isVisible ? "opacity-100 animate-slide-down" : "opacity-0"}`}
    >
      <div
        className={`relative w-full pointer-events-auto flex items-start justify-between gap-3 rounded-[8px] p-3 md:py-4 overflow-hidden ${bgColor} ${actionText && "hover:shadow-sm"}`}
        style={toastMaxWidth}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
      >
        <div // Toast duration loader - remove hidden from class to activate
          className={`hidden absolute z-[100] left-0 bottom-0 h-[2px] bg-opacity-30 ${loadingColor}`}
          style={toastLoaderStyle}
        />
        <div className="h-full">{icon}</div>
        <div className="flex-1 flex flex-col items-start h-full">
          <div
            className={`${!actionText && "max-sm:pt-[1px]"} ${textColor} xl:text-[18px] md:text-[16px] text-[14px] font-medium leading-[1.2]`}
          >
            {message}
          </div>
          {actionText && onActionClick && (
            <button
              className={`${textColor} underline xl:text-[18px] md:text-[16px] text-[14px] font-medium mt-2`}
              onClick={(e) => {
                e.stopPropagation();
                onActionClick();
                handleClose();
              }}
            >
              {actionText}
            </button>
          )}
        </div>
        {onClose && (
          <button
            className={`${textColor}`}
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
          >
            <CloseIcon className="w-4 h-4 md:w-5 md:h-5" />
          </button>
        )}
      </div>
    </div>
  );
};

const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<
    Array<{ id: number; props: ToastProps }>
  >([]);

  const showToast = (props: ToastProps) => {
    const id = Date.now();
    setToasts((prevToasts) => [...prevToasts, { id, props }]);
    return id;
  };

  const removeToast = (id: number) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  };

  return (
    <>
      {toasts.map(({ id, props }) => (
        <Toast
          key={id}
          {...props}
          onClose={() => {
            if (props.onClose) props.onClose();
            removeToast(id);
          }}
        />
      ))}
    </>
  );
};

// Create a Toast Context for global usage
const ToastContext = React.createContext<{
  showToast: (props: ToastProps) => number;
  hideToast: (id: number) => void;
}>({
  showToast: () => 0,
  hideToast: () => {},
});

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<
    Array<{ id: number; props: ToastProps }>
  >([]);

  const showToast = (props: ToastProps) => {
    const id = Date.now();
    setToasts((prevToasts) => [...prevToasts, { id, props }]);
    return id;
  };

  const hideToast = (id: number) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  };

  useEffect(() => {
    setToastHandler((props: ToastProps) => {
      showToast(props); // Directly use the props passed
    });
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      {toasts.map(({ id, props }) => (
        <Toast
          key={id}
          {...props}
          onClose={() => {
            if (props.onClose) props.onClose();
            hideToast(id);
          }}
        />
      ))}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = React.useContext(ToastContext);
  if (context === undefined) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

export default ToastContainer;
